import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { canCreateOnBoard } from '../shared/index.js';

import { ApiError } from '../api/client.js';
import type { BoardView, CardPlacement } from '../api/endpoints.js';
import { ticketApi } from '../api/endpoints.js';
import { applyCardMove } from '../app/card-move.js';
import { EMPTY_FILTERS, applyFilters } from '../app/filters.js';
import type { BoardFilters, BoardViewMode } from '../app/filters.js';
import { queryKeys } from '../app/query-client.js';
import { useSession } from '../auth/session.js';
import { CreateTicket } from '../components/create-ticket.js';
import { FilterBar } from '../components/filter-bar.js';
import { Kanban, resolveCardDrop } from '../components/kanban.js';
import { TicketCard } from '../components/ticket-card.js';
import { PageHeader } from '../components/page-header.js';
import { TicketList } from '../components/ticket-list.js';
import { TicketPanel } from '../components/ticket-panel.js';
import { Alert, Button, Spinner } from '../components/ui.js';
import {
  useArchivedCards,
  useBoardView,
  useBoards,
  useDirectoryIndex,
} from '../hooks/use-directory.js';

const DROP_SETTLE_MS = 450;

interface MoveVariables {
  readonly ticketId: string;
  readonly placement: CardPlacement;
  readonly previous: BoardView | undefined;
}

export function BoardPage(): ReactNode {
  const { slug } = useParams<{ slug: string }>();
  const { principal } = useSession();
  const queryClient = useQueryClient();

  const { boards, isLoading: boardsLoading } = useBoards();
  const summary = boards.find((entry) => entry.board.slug === slug);
  const { view, isLoading } = useBoardView(summary?.board.id);

  const people = useDirectoryIndex();

  const [filters, setFilters] = useState<BoardFilters>(EMPTY_FILTERS);
  const [filtersBelongTo, setFiltersBelongTo] = useState(slug);
  const [mode, setMode] = useState<BoardViewMode>('board');

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTicketId = searchParams.get('ticket');
  const [openTicketId, setOpenTicketId] = useState<string | null>(requestedTicketId);
  const [openedFromLink, setOpenedFromLink] = useState(requestedTicketId);

  if (requestedTicketId !== openedFromLink) {
    setOpenedFromLink(requestedTicketId);
    setOpenTicketId(requestedTicketId);
  }

  const closeTicket = (): void => {
    setOpenTicketId(null);

    if (searchParams.has('ticket')) {
      const next = new URLSearchParams(searchParams);
      next.delete('ticket');
      setSearchParams(next, { replace: true });
    }
  };
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (slug !== filtersBelongTo) {
    setFiltersBelongTo(slug);
    setFilters(EMPTY_FILTERS);
    setProblem(null);
    setMode('board');
    setOpenTicketId(requestedTicketId);
  }

  const { view: archivedView, isLoading: archivedLoading } = useArchivedCards(
    summary?.board.id,
    mode === 'archived',
  );

  const tickets = useMemo(() => view?.items ?? [], [view]);
  const archived = useMemo(() => archivedView?.items ?? [], [archivedView]);

  const source = mode === 'archived' ? archived : tickets;
  const shown = useMemo(() => applyFilters(source, filters, people), [source, filters, people]);

  useEffect(() => {
    return () => {};
  }, [slug]);

  const restore = useMutation({
    mutationFn: (ticketId: string) => ticketApi.unarchive(ticketId),
    onSuccess: async () => {
      setProblem(null);
      if (summary === undefined) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.archived(summary.board.id) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.cards(summary.board.id) }),
      ]);
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That request could not be restored.');
    },
  });

  const move = useMutation({
    mutationFn: ({ ticketId, placement }: MoveVariables) => ticketApi.move(ticketId, placement),

    onSuccess: () => {
      setProblem(null);
    },

    onError: (error: unknown, variables) => {
      if (variables.previous !== undefined && summary !== undefined) {
        queryClient.setQueryData<BoardView>(
          queryKeys.boards.cards(summary.board.id),
          variables.previous,
        );
      }

      setProblem(error instanceof ApiError ? error.message : 'That card could not be moved.');
    },

    onSettled: async () => {
      await new Promise((resolve) => {
        window.setTimeout(resolve, DROP_SETTLE_MS);
      });

      if (summary !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.boards.cards(summary.board.id) });
      }
    },
  });

  const applyAndSend = (ticketId: string, placement: CardPlacement): void => {
    if (summary === undefined || view === undefined) return;

    const key = queryKeys.boards.cards(summary.board.id);
    const previous = queryClient.getQueryData<BoardView>(key);

    if (previous !== undefined) {
      queryClient.setQueryData<BoardView>(key, {
        ...previous,
        items: applyCardMove(previous.items, ticketId, placement, previous.workflow),
      });
    }

    move.mutate({ ticketId, placement, previous });
  };

  const loading = boardsLoading || (isLoading && view === undefined);
  const ready = !loading && summary !== undefined && view !== undefined && principal !== null;

  return (
    <DragDropProvider
      onDragEnd={(event) => {
        if (view === undefined) return;

        const drop = resolveCardDrop(event, shown, view.workflow);

        if (drop !== null) applyAndSend(drop.ticketId, drop.placement);
      }}
    >
      {loading ? (
        <div className="grid flex-1 place-items-center text-muted">
          <Spinner className="size-6" />
        </div>
      ) : !ready ? (
        <div className="grid flex-1 place-items-center px-8 text-center">
          <div>
            <h1 className="font-display text-[24px] font-extrabold text-navy dark:text-dark-ink">
              That board is not open to you
            </h1>
            <p className="mt-2 text-[14px] text-muted">
              Either it does not exist, or your role does not reach it.
            </p>
          </div>
        </div>
      ) : (
        <>
          <PageHeader title={summary.board.name} subtitle={summary.board.description}>
            {canCreateOnBoard(principal, summary.board) ? (
              <Button
                size="sm"
                onClick={() => {
                  setCreating(true);
                }}
              >
                <span className="hidden sm:inline">New request</span>
                <span className="sm:hidden">New</span>
              </Button>
            ) : null}
          </PageHeader>

          <FilterBar
            filters={filters}
            onChange={setFilters}
            view={mode}
            onViewChange={setMode}
            tickets={source}
            total={source.length}
            shown={shown.length}
          />

          {problem === null ? null : (
            <div className="px-4 pt-4 sm:px-7">
              <Alert tone="error">{problem}</Alert>
            </div>
          )}

          {mode === 'board' ? (
            <Kanban
              workflow={view.workflow}
              tickets={shown}
              principal={principal}
              people={people}
              onOpen={setOpenTicketId}
            />
          ) : mode === 'list' ? (
            <TicketList
              workflow={view.workflow}
              tickets={shown}
              people={people}
              onOpen={setOpenTicketId}
            />
          ) : archivedLoading ? (
            <div className="grid flex-1 place-items-center text-muted">
              <Spinner className="size-6" />
            </div>
          ) : shown.length === 0 ? (
            <div className="grid flex-1 place-items-center px-8 text-center">
              <p className="max-w-sm text-[13.5px] leading-relaxed text-muted">
                Nothing has been archived on this board yet. Archiving a request takes it off the
                board without deleting it — you can always restore it from here.
              </p>
            </div>
          ) : (
            <TicketList
              workflow={view.workflow}
              tickets={shown}
              people={people}
              onOpen={setOpenTicketId}
              renderAction={(ticket) => (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={restore.isPending && restore.variables === ticket.id}
                  onClick={() => {
                    restore.mutate(ticket.id);
                  }}
                >
                  Restore
                </Button>
              )}
            />
          )}

          {openTicketId === null ? null : (
            <TicketPanel
              ticketId={openTicketId}
              boardId={summary.board.id}
              board={summary.board}
              workflow={view.workflow}
              principal={principal}
              people={people}
              onClose={closeTicket}
            />
          )}

          {creating ? (
            <CreateTicket
              board={summary.board}
              onClose={() => {
                setCreating(false);
              }}
              onCreated={(ticketId) => {
                setCreating(false);
                setOpenTicketId(ticketId);
              }}
            />
          ) : null}
        </>
      )}

      <DragOverlay>
        {(source) => {
          const card = shown.find((ticket) => ticket.id === String(source.id));

          if (card === undefined) return null;

          return (
            <TicketCard
              ticket={card}
              people={people}
              onOpen={() => {}}
            />
          );
        }}
      </DragOverlay>
    </DragDropProvider>
  );
}
